import React, { useState, useEffect, useCallback } from 'react';
import { 
  ICheckboxComponent,
  IFormControlChange,
  IFormValidationErrors
} from '../df-form-preview-interfaces';
import { DfFormErrorMsg } from '../df-form-error-msg';

interface DfFormCheckboxProps {
  id: string;
  properties: ICheckboxComponent;
  validationErrors?: IFormValidationErrors;
  formValue?: string[];
  readonly?: boolean;
  disabled?: boolean;
  touchedFields?: Record<string, boolean>;
  formSubmitted?: boolean;
  mode?: 'edit' | 'preview' | 'test';
  onValueChange?: (change: IFormControlChange) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  hideLabel?: boolean;
  showCommentsInPreview?: boolean;
}

export const DfFormCheckbox: React.FC<DfFormCheckboxProps> = ({
  id,
  properties,
  validationErrors = {},
  formValue = [],
  readonly = false,
  disabled = false,
  touchedFields = {},
  formSubmitted = false,
  mode = 'preview',
  onValueChange,
  onBlur,
  onFocus,
  className = '',
  hideLabel = false
}) => {
  const [isTouched, setIsTouched] = useState<boolean>(false);
  
  // Use formValue directly instead of local state to avoid conflicts
  const selectedValues = Array.isArray(formValue) ? formValue : [];

  // Validate field
  const validateField = useCallback((values: string[]): { isValid: boolean; errors: Record<string, any> } => {
    const errors: Record<string, any> = {};
    let isValid = true;

    // Required validation
    if (properties?.validation?.required && values.length === 0) {
      errors.required = true;
      isValid = false;
    }

    return { isValid, errors };
  }, [properties]);

  // Handle value changes
  const handleValueChange = useCallback((newValues: string[]) => {
    
    if (onValueChange) {
      const validation = validateField(newValues);
      const change: IFormControlChange = {
        id,
        value: newValues,
        isValid: validation.isValid,
        errors: validation.errors
      };
      onValueChange(change);
    }
  }, [id, onValueChange, validateField, selectedValues]);

  // Handle checkbox change
  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    
    let newValues: string[];
    
    if (checked) {
      newValues = [...selectedValues, optionValue];
    } else {
      newValues = selectedValues.filter(value => value !== optionValue);
    }
    
    handleValueChange(newValues);
  };

  // Handle focus
  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  // Handle blur
  const handleBlur = () => {
    setIsTouched(true);
    if (onBlur) {
      onBlur();
    }
  };

  // Update touched fields
  useEffect(() => {
    if (isTouched) {
      touchedFields[id] = true;
    }
  }, [isTouched, id, touchedFields]);

  // Log formValue changes for debugging
  useEffect(() => {
  }, [formValue, id, selectedValues]);

  // Mark as touched when form is submitted
  useEffect(() => {
    if (formSubmitted) {
      setIsTouched(true);
    }
  }, [formSubmitted]);

  // Get label alignment class
  const getLabelAlignmentClass = (): string => {
    const alignment = properties?.styles?.labelAlignment;
    return alignment === 'left' ? 'label-left' : 'label-top';
  };

  // Get layout class for inline/vertical layout
  const getLayoutClass = (): string => {
    return properties?.basic?.inlineLayout ? 'inline-layout' : 'vertical-layout';
  };


  const shouldDisable = disabled || readonly || properties?.validation?.readonly;

  return (
    <>
    <div className={`form-group checkbox-group ${getLabelAlignmentClass()} ${getLayoutClass()} ${className}`}>
      {!hideLabel && properties.basic.label ? (
        <>
          <label className="form-label">
            {properties.basic.label}
            {properties.validation.required && (
              <span className="required-indicator">*</span>
            )}
          </label>
          
          <div className="input-wrapper">
            <div className={`mt-2 ${properties?.basic?.inlineLayout ? 'inline-options-grid' : 'space-y-2'}`}>
              {(properties.options || []).map((option, index) => (
                <div key={index} className="form-check">
                  <input
                    type="checkbox"
                    id={`${id}-${index}`}
                    name={id}
                    value={option.value}
                    checked={selectedValues.includes(option.value)}
                    onChange={(e) => {
                      handleCheckboxChange(option.value, e.target.checked);
                    }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    disabled={shouldDisable || option.disabled}
                    className="form-check-input"
                  />
                  <label 
                    htmlFor={`${id}-${index}`} 
                    className="form-check-label"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
            <DfFormErrorMsg
        validationErrors={validationErrors}
        fieldId={id}
        touchedFields={touchedFields}
        formSubmitted={formSubmitted}
        properties={properties}
        localValidation={validateField(selectedValues)}
        isTouched={isTouched}
        mode={mode}
      />
          </div>
        </>
      ) : (
        <div className="input-wrapper">
          <div className={`mt-2 ${properties?.basic?.inlineLayout ? 'inline-options-grid' : 'space-y-2'}`}>
            {(properties.options || []).map((option, index) => (
              <div key={index} className="form-check">
                <input
                  type="checkbox"
                  id={`${id}-${index}`}
                  name={id}
                  value={option.value}
                  checked={selectedValues.includes(option.value)}
                  onChange={(e) => {
                    handleCheckboxChange(option.value, e.target.checked);
                  }}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  disabled={shouldDisable || option.disabled}
                  className="form-check-input"
                />
                <label 
                  htmlFor={`${id}-${index}`} 
                  className="form-check-label"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
          <DfFormErrorMsg
        validationErrors={validationErrors}
        fieldId={id}
        touchedFields={touchedFields}
        formSubmitted={formSubmitted}
        properties={properties}
        localValidation={validateField(selectedValues)}
        isTouched={isTouched}
        mode={mode}
      />
        </div>
      )}

    </div>
    </>
  );
};

export default DfFormCheckbox;
